import { useEffect, useRef } from "react";

/**
 * Closes the panel when the user clicks outside the referenced element.
 *
 * Implementation note: react-datepicker (and a few other inner pickers used
 * by the search bar) re-renders synchronously when a date or option is
 * clicked, which detaches the clicked node from the DOM before the document
 * `click` listener fires. That made the old `ref.current.contains(target)`
 * check report "outside" for clicks that were actually inside the panel —
 * so the panel closed without committing the selection.
 *
 * The fix:
 *   1. Listen on `mousedown` — fires before React's synchronous re-render,
 *      so the target is still attached.
 *   2. Use `event.composedPath()` — captured at dispatch time and survives
 *      later DOM mutations, in case any handler still detaches the node.
 *   3. Fall back to `contains()` on browsers without composedPath.
 *
 * Happy-path behaviour is unchanged: outside clicks still close the panel,
 * inside clicks still don't. Only the previously-broken case (a descendant
 * clicked just before being unmounted) is now correctly classified.
 */
export const useClickOutside = (
  ref: React.RefObject<HTMLElement>,
  isOpen: boolean,
  onClose: () => void
) => {
  const enabledRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      enabledRef.current = false;
      return;
    }

    // Delay enabling the handler until AFTER the event loop tick so the
    // opening click itself doesn't trigger a close.
    const t = setTimeout(() => {
      enabledRef.current = true;
    }, 0);

    const handlePointer = (event: MouseEvent) => {
      if (!enabledRef.current) return;
      const node = ref.current;
      if (!node) return;

      const path =
        typeof (event as any).composedPath === "function"
          ? ((event as any).composedPath() as EventTarget[])
          : null;

      if (path && path.includes(node)) return;
      if (!path && node.contains(event.target as Node)) return;

      onClose();
    };

    document.addEventListener("mousedown", handlePointer);

    return () => {
      enabledRef.current = false;
      clearTimeout(t);
      document.removeEventListener("mousedown", handlePointer);
    };
  }, [isOpen, ref, onClose]);
};
